import { useMemo, useState } from 'react'
import { styled } from '@linaria/react'
import { minimalVars } from '@ovasabi/ui-minimal/tokens'
import { MinimalButton, MinimalCard } from '@ovasabi/ui-minimal'
import { useNavigate } from 'react-router-dom'
import { useVendorProfileSnapshot } from '../../generated/prototypeRuntime'
import { displayTitle } from '../../lib/displayName'
import { kitchenPath } from '../../lib/slug'
import { useChooseChow } from './ChooseChowContext'
import { copyKitchenLink, kitchenUrl, shareKitchen, type ShareableKitchen } from './kitchenShare'

const Link = styled.output`
  display: block;
  margin: ${minimalVars.space.xs} 0;
  padding: ${minimalVars.space.xs} ${minimalVars.space.sm};
  border: 1px solid ${minimalVars.color.borderSubtle};
  border-radius: ${minimalVars.radius.md};
  background: ${minimalVars.color.bgSurface};
  font-size: ${minimalVars.typography.captionSize};
  color: ${minimalVars.color.textPrimary};
  overflow-wrap: anywhere;
  user-select: all;
`

const Hint = styled.p`
  margin: 2px 0 0;
  font-size: ${minimalVars.typography.captionSize};
  color: ${minimalVars.color.textSecondary};
`

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${minimalVars.space.xs};
`

/** The signed-in chef's own kitchen, as the public link names it. */
const useOwnKitchen = (): (ShareableKitchen & { verified: boolean }) | undefined => {
  const { profileId } = useChooseChow()
  const vendors = useVendorProfileSnapshot() as { records: readonly Record<string, unknown>[] }
  return useMemo(() => {
    // A synthetic `vendor-<profileId>` row has no public id, so no public link.
    const row = vendors.records.find(
      (v) => String(v.profile_id ?? '') === profileId && !String(v.id ?? '').startsWith('vendor-'),
    )
    if (!row?.id) return undefined
    return {
      id: String(row.id),
      name: displayTitle(row.display_name) || 'My kitchen',
      cuisine: String(row.cuisine ?? ''),
      area: String(row.location_label ?? ''),
      verified: String(row.verification_status ?? '').toLowerCase() === 'verified',
    }
  }, [vendors.records, profileId])
}

/**
 * "Where is my kitchen link?" — the chef could only find a Share button by
 * visiting their own public page as a customer would, which nothing in the
 * chef's app pointed to. This puts the link where the chef already is, with the
 * phone's share sheet one tap away.
 */
export const ShareKitchenCard = () => {
  const navigate = useNavigate()
  const kitchen = useOwnKitchen()
  const [note, setNote] = useState('')
  if (!kitchen) return null

  const flash = (text: string) => {
    setNote(text)
    setTimeout(() => setNote(''), 2400)
  }
  const share = async () => {
    const outcome = await shareKitchen(kitchen, true)
    if (outcome === 'copied') flash('Link copied. Paste it anywhere.')
    if (outcome === 'failed') flash('Couldn’t share. Press and hold the link to copy it.')
  }
  const copy = async () => {
    flash((await copyKitchenLink(kitchen)) ? 'Link copied. Paste it anywhere.' : 'Press and hold the link to copy it.')
  }
  const url = kitchenUrl(kitchen)

  return (
    <MinimalCard padding="md" enter={false}>
      <h3 style={{ margin: 0, fontSize: 'inherit' }}>Your kitchen link</h3>
      <Hint>Share it on WhatsApp, Instagram or your status. Anyone who taps it sees your menu and can order.</Hint>
      <Link aria-label="Your kitchen link">{url.replace(/^https?:\/\//, '')}</Link>
      {!kitchen.verified && <Hint>Customers can order once your kitchen is verified.</Hint>}
      <Actions>
        <MinimalButton size="sm" onClick={() => void share()}>Share</MinimalButton>
        <MinimalButton size="sm" variant="secondary" onClick={() => void copy()}>Copy link</MinimalButton>
        <MinimalButton size="sm" variant="ghost" onClick={() => navigate(kitchenPath(kitchen))}>See what customers see</MinimalButton>
      </Actions>
      {note && <Hint role="status">{note}</Hint>}
    </MinimalCard>
  )
}
